import React, { useState } from 'react';
import clsx from 'clsx';
import { defineMessages, useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the panel; each part uses its own slice of it */
import styles from './DayPanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

const messages = defineMessages({
  label: { id: 'app.sml.promote.calendar.kv.caption', defaultMessage: 'Caption' },
  empty: { id: 'app.sml.promote.calendar.caption.empty', defaultMessage: 'No caption' },
  showAll: { id: 'app.sml.promote.calendar.caption.showAll', defaultMessage: 'Show all' },
  showLess: { id: 'app.sml.promote.calendar.caption.showLess', defaultMessage: 'Show less' },
});

/** A caption longer than this, or with more lines, starts closed with "Show all". */
const SHORT_CAPTION_CHARACTERS = 220;
const SHORT_CAPTION_LINES = 4;

/** True when the caption fits in its first lines and needs no "Show all". Pure. */
export function isShortCaption(caption: string): boolean {
  return caption.length <= SHORT_CAPTION_CHARACTERS && caption.split('\n').length <= SHORT_CAPTION_LINES;
}

interface PostCaptionProps {
  caption: string;
}

/**
 * The caption of the open post in the Calendar panel (QA finding B13): the first lines, and
 * "Show all" for a long caption.
 */
const PostCaptionComponent: React.FC<PostCaptionProps> = ({ caption }) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const text = caption.trim();
  const isShort = isShortCaption(text);

  return (
    <div data-testid="day-panel-caption">
      <div className="small text-muted mb-1">{intl.formatMessage(messages.label)}</div>
      {text === '' ? (
        <div className={clsx(styles.postCaption, 'text-muted')}>{intl.formatMessage(messages.empty)}</div>
      ) : (
        <div className={clsx(styles.postCaption, !isShort && !isOpen && styles.postCaptionClamped)}>{text}</div>
      )}
      {isShort ? null : (
        <button
          type="button"
          className="btn btn-link btn-sm p-0"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
        >
          {intl.formatMessage(isOpen ? messages.showLess : messages.showAll)}
        </button>
      )}
    </div>
  );
};

export const PostCaption = TypedMemo(PostCaptionComponent);
